import { useState, useMemo, useEffect } from 'react';
import axios from '../axios.jsx';
import { Button, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLOR_BARRA = ['#1f3a5f', '#1e7e42', '#b15a00', '#c0392b', '#2f5c93', '#8a94a6', '#6b7280', '#7a4fc9'];
const COLOR_SI = '#c0392b';
const COLOR_NO = '#1e7e42';
const COLOR_SIN_DATO = '#d1d5db';

const hoyISO = () => new Date().toISOString().split('T')[0];

const PanelFormatoBAC = () => {
  /* ── Selección de proyecto ── */
  const [proyectos, setProyectos] = useState([]);
  const [showSelector, setShowSelector] = useState(true);
  const [proyecto, setProyecto] = useState(null); // { key, nombre, codigoCorto }
  const [cargandoProyectos, setCargandoProyectos] = useState(true);

  /* ── Fechas / carga ── */
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [descargando, setDescargando] = useState(false);
  const [cargandoDashboard, setCargandoDashboard] = useState(false);
  const [stats, setStats] = useState(null);

  /* ── Búsqueda de personas ── */
  const [showBuscar, setShowBuscar] = useState(false);
  const [queryBusqueda, setQueryBusqueda] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultadosBusqueda, setResultadosBusqueda] = useState(null); // null = aún no se ha buscado
  const [expandido, setExpandido] = useState(null); // id del resultado con detalle abierto

  /* ───────────────────────── Cargar lista de proyectos ─────────────── */
  useEffect(() => {
    axios
      .get('export-epicollect/proyectos')
      .then(({ data }) => setProyectos(data))
      .catch(() => Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar la lista de proyectos.' }))
      .finally(() => setCargandoProyectos(false));
  }, []);

  /* ───────────────────────── Elegir proyecto ───────────────────────── */
  const handleElegirProyecto = async (p) => {
    setProyecto(p);
    setShowSelector(false);
    setStats(null);

    try {
      const { data } = await axios.get('export-epicollect/info', { params: { proyecto: p.key } });
      setFechaInicio(data.ultimoRegistro || hoyISO());
      setFechaFin(hoyISO());
    } catch {
      setFechaInicio(hoyISO());
      setFechaFin(hoyISO());
    }
  };

  const fechasValidas = () => {
    if (!fechaInicio || !fechaFin) {
      Swal.fire({ icon: 'warning', title: 'Selecciona ambas fechas' });
      return false;
    }
    if (fechaInicio > fechaFin) {
      Swal.fire({ icon: 'warning', title: 'Rango inválido', text: 'La fecha inicio no puede ser mayor a la fecha fin.' });
      return false;
    }
    return true;
  };

  /* ───────────────────────── Descargar Excel ───────────────────────── */
  const handleDescargar = async () => {
    if (!fechasValidas()) return;

    setDescargando(true);
    try {
      const response = await axios.get('export-epicollect/formato-bac', {
        params: { proyecto: proyecto.key, fechaInicio, fechaFin },
        responseType: 'blob',
      });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', `${proyecto.codigoCorto}_${fechaInicio}_a_${fechaFin}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      if (error.response?.status === 404) {
        Swal.fire({ icon: 'info', title: 'Sin resultados', text: 'No hay registros en el rango de fechas seleccionado.' });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el archivo Excel.' });
      }
    } finally {
      setDescargando(false);
    }
  };

  /* ───────────────────────── Generar Dashboard ─────────────────────── */
  const handleGenerarDashboard = async () => {
    if (!fechasValidas()) return;

    setCargandoDashboard(true);
    setStats(null);
    try {
      const { data } = await axios.get('export-epicollect/dashboard', {
        params: { proyecto: proyecto.key, fechaInicio, fechaFin },
      });
      setStats(data);
    } catch (error) {
      if (error.response?.status === 404) {
        Swal.fire({ icon: 'info', title: 'Sin resultados', text: 'No hay registros en el rango de fechas seleccionado.' });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el dashboard.' });
      }
    } finally {
      setCargandoDashboard(false);
    }
  };

  /* ───────────────────────── Buscar persona ────────────────────────── */
  const abrirBuscar = () => {
    setQueryBusqueda('');
    setResultadosBusqueda(null);
    setExpandido(null);
    setShowBuscar(true);
  };

  const handleBuscar = async () => {
    const query = queryBusqueda.trim();
    if (!query) {
      Swal.fire({ icon: 'warning', title: 'Escribe un nombre o número de identificación' });
      return;
    }

    setBuscando(true);
    setResultadosBusqueda(null);
    try {
      const { data } = await axios.get('export-epicollect/buscar', {
        params: { proyecto: proyecto.key, query },
      });
      setResultadosBusqueda(data.resultados || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudo realizar la búsqueda.',
      });
      setResultadosBusqueda([]);
    } finally {
      setBuscando(false);
    }
  };

  /* ───────────────────────── Datos derivados ───────────────────────── */

  const heatmapData = useMemo(() => {
    if (!stats?.heatmap) return null;
    const anios = {};
    let max = 0;

    Object.entries(stats.heatmap).forEach(([clave, valor]) => {
      const [anio, mes] = clave.split('-');
      if (!anios[anio]) anios[anio] = Array(12).fill(0);
      anios[anio][parseInt(mes, 10) - 1] = valor;
      if (valor > max) max = valor;
    });

    const aniosOrdenados = Object.keys(anios).sort();
    return { anios, aniosOrdenados, max };
  }, [stats]);

  const colorCelda = (valor, max) => {
    if (!valor) return '#f1f3f5';
    const ratio = max > 0 ? valor / max : 0;
    if (ratio > 0.75) return '#1e5f8c';
    if (ratio > 0.5) return '#2f7fb3';
    if (ratio > 0.25) return '#6ab0d8';
    return '#bfe0f0';
  };

  const barraData = useMemo(() => {
    if (!stats?.graficoBarra?.datos) return [];
    return stats.graficoBarra.datos.map((d, idx) => ({ ...d, fill: COLOR_BARRA[idx % COLOR_BARRA.length] }));
  }, [stats]);

  const categoriasData = stats?.graficoCategorias?.datos || [];

  const edadData = useMemo(() => {
    if (!stats?.graficoEdad?.datos) return [];
    return stats.graficoEdad.datos.map((d, idx) => ({ ...d, fill: COLOR_BARRA[idx % COLOR_BARRA.length] }));
  }, [stats]);

  const sintomasData = useMemo(() => {
    if (!stats?.graficoSintomas?.datos) return [];
    return stats.graficoSintomas.datos.map((d, idx) => ({ ...d, fill: COLOR_BARRA[idx % COLOR_BARRA.length] }));
  }, [stats]);

  const categoriasSecundariasData = stats?.graficoCategoriasSecundario?.datos || [];

  const graficosExtra = stats?.graficosExtra || [];

  const donutData = useMemo(() => {
    if (!stats?.graficoDonut) return [];
    const { si, no, sinDato, etiquetaSi, etiquetaNo } = stats.graficoDonut;
    return [
      { nombre: etiquetaSi || 'Sí', valor: si, color: COLOR_SI },
      { nombre: etiquetaNo || 'No', valor: no, color: COLOR_NO },
      ...(sinDato > 0 ? [{ nombre: 'Sin dato', valor: sinDato, color: COLOR_SIN_DATO }] : []),
    ];
  }, [stats]);

  const donutTotal = donutData.reduce((acc, d) => acc + d.valor, 0);
  const donutPctSi = donutTotal ? Math.round(((donutData[0]?.valor || 0) / donutTotal) * 100) : 0;

  /* ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="container-fluid panel-bac">
      <style>{`
        .panel-bac {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1.5rem;
        }

        .panel-bac h3 {
          font-weight: 700;
          color: #1f3a5f;
          margin-bottom: 0.25rem;
        }

        .panel-bac .subtitle {
          color: #8a94a6;
          margin-bottom: 1.5rem;
          font-size: 0.92rem;
        }

        .panel-bac .toolbar-card {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.08);
          padding: 1.25rem 1.5rem;
          margin-bottom: 1.75rem;
          display: flex;
          align-items: flex-end;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .panel-bac .toolbar-card label {
          font-weight: 600;
          color: #334155;
          font-size: 0.85rem;
          margin-bottom: 4px;
          display: block;
        }

        .panel-bac .toolbar-card .form-control {
          border-radius: 8px;
        }

        .panel-bac .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.75rem;
        }

        .panel-bac .kpi-card {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 2px 14px rgba(0,0,0,0.07);
          padding: 1.1rem 1.3rem;
          border-left: 5px solid #1f3a5f;
        }

        .panel-bac .kpi-card:nth-child(2) { border-left-color: #c0392b; }
        .panel-bac .kpi-card:nth-child(3) { border-left-color: #1e7e42; }
        .panel-bac .kpi-card:nth-child(4) { border-left-color: #2f5c93; }

        .panel-bac .kpi-card .kpi-label {
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #8a94a6;
          font-weight: 700;
          margin-bottom: 6px;
        }

        .panel-bac .kpi-card .kpi-value {
          font-size: 1.9rem;
          font-weight: 800;
          color: #1f3a5f;
          line-height: 1;
        }

        .panel-bac .grid-2 {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.75rem;
        }

        @media (max-width: 900px) {
          .panel-bac .grid-2 {
            grid-template-columns: 1fr;
          }
        }

        .panel-bac .card-block {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.08);
          padding: 1.5rem;
        }

        .panel-bac .card-title {
          font-weight: 700;
          color: #1f3a5f;
          margin-bottom: 1.1rem;
          font-size: 1.02rem;
        }

        .panel-bac .heatmap-scroll { overflow-x: auto; }

        .panel-bac .heatmap-table {
          border-collapse: separate;
          border-spacing: 4px;
          min-width: 560px;
        }

        .panel-bac .heatmap-table th {
          font-size: 0.72rem;
          color: #8a94a6;
          font-weight: 700;
          text-transform: uppercase;
          padding-bottom: 4px;
        }

        .panel-bac .heatmap-table td.year-label {
          font-size: 0.8rem;
          font-weight: 700;
          color: #334155;
          padding-right: 8px;
          white-space: nowrap;
        }

        .panel-bac .heatmap-cell {
          width: 34px;
          height: 34px;
          border-radius: 6px;
          text-align: center;
          vertical-align: middle;
          font-size: 0.68rem;
          font-weight: 700;
          color: #fff;
          transition: transform 0.1s ease-in-out;
        }

        .panel-bac .heatmap-cell:hover { transform: scale(1.12); }
        .panel-bac .heatmap-cell.empty { color: #c3c9d1; }

        .panel-bac .heatmap-legend {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 1rem;
          font-size: 0.75rem;
          color: #8a94a6;
        }

        .panel-bac .heatmap-legend .swatch {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          display: inline-block;
        }

        .panel-bac .donut-center-label {
          text-align: center;
          margin-top: -1.2rem;
          margin-bottom: 0.8rem;
        }

        .panel-bac .donut-center-label .pct {
          font-size: 1.6rem;
          font-weight: 800;
          color: #1f3a5f;
          display: block;
        }

        .panel-bac .donut-center-label small {
          color: #8a94a6;
          font-weight: 600;
          font-size: 0.72rem;
        }

        .panel-bac .empty-state {
          text-align: center;
          color: #8a94a6;
          font-style: italic;
          padding: 2rem;
        }

        .panel-bac .proyecto-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #eef2ff;
          color: #1f3a5f;
          font-weight: 700;
          font-size: 0.78rem;
          padding: 4px 12px;
          border-radius: 999px;
          margin-left: 10px;
          cursor: pointer;
          border: none;
        }

        .selector-proyecto-card {
          border: 2px solid #e7ebf0;
          border-radius: 14px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.15s ease-in-out;
          height: 100%;
        }

        .selector-proyecto-card:hover {
          border-color: #1f3a5f;
          background: #f4f8fc;
          transform: translateY(-2px);
        }

        .selector-proyecto-card .codigo {
          display: inline-block;
          background: #1f3a5f;
          color: #fff;
          font-weight: 800;
          font-size: 0.85rem;
          padding: 6px 14px;
          border-radius: 999px;
          margin-bottom: 0.75rem;
        }

        .selector-proyecto-card .nombre {
          font-weight: 600;
          color: #334155;
        }

        /* ── Modal de búsqueda ── */
        .buscar-input-group {
          display: flex;
          gap: 8px;
          margin-bottom: 1.25rem;
        }

        .buscar-input-group .form-control {
          border-radius: 8px;
        }

        .buscar-resultados {
          max-height: 55vh;
          overflow-y: auto;
          padding-right: 4px;
        }

        .resultado-card {
          border: 1px solid #e7ebf0;
          border-radius: 12px;
          padding: 1rem 1.2rem;
          margin-bottom: 0.75rem;
          transition: box-shadow 0.15s ease-in-out;
        }

        .resultado-card:hover {
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }

        .resultado-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          cursor: pointer;
        }

        .resultado-nombre {
          font-weight: 700;
          color: #1f3a5f;
          font-size: 1rem;
        }

        .resultado-meta {
          font-size: 0.82rem;
          color: #8a94a6;
          margin-top: 2px;
        }

        .resultado-meta b {
          color: #334155;
        }

        .resultado-toggle {
          color: #1f3a5f;
          font-size: 0.9rem;
          flex-shrink: 0;
        }

        .resultado-detalle {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px dashed #e7ebf0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 0.6rem 1.2rem;
        }

        .resultado-detalle .campo-label {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #8a94a6;
          font-weight: 700;
          margin-bottom: 1px;
        }

        .resultado-detalle .campo-valor {
          font-size: 0.88rem;
          color: #334155;
          word-break: break-word;
        }

        .buscar-contador {
          font-size: 0.8rem;
          color: #8a94a6;
          margin-bottom: 0.75rem;
        }
      `}</style>

      {/* ── Modal de selección de proyecto ── */}
      <Modal show={showSelector} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>¿Qué proyecto deseas consultar?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cargandoProyectos ? (
            <p className="text-center text-muted">Cargando proyectos...</p>
          ) : (
            <div className="row g-3">
              {proyectos.map((p) => (
                <div className="col-6" key={p.key}>
                  <div className="selector-proyecto-card" onClick={() => handleElegirProyecto(p)}>
                    <span className="codigo">{p.codigoCorto}</span>
                    <div className="nombre">{p.nombre}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ── Modal de búsqueda de personas ── */}
      <Modal show={showBuscar} onHide={() => setShowBuscar(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-magnifying-glass me-2"></i>
            Buscar Persona
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="buscar-input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Nombre o número de identificación..."
              value={queryBusqueda}
              onChange={(e) => setQueryBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
              autoFocus
            />
            <Button variant="primary" onClick={handleBuscar} disabled={buscando}>
              {buscando ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>

          {buscando && <p className="text-center text-muted">Buscando...</p>}

          {!buscando && resultadosBusqueda && resultadosBusqueda.length === 0 && (
            <div className="empty-state">No se encontraron coincidencias.</div>
          )}

          {!buscando && resultadosBusqueda && resultadosBusqueda.length > 0 && (
            <>
              <div className="buscar-contador">
                {resultadosBusqueda.length} resultado{resultadosBusqueda.length !== 1 ? 's' : ''}
                {resultadosBusqueda.length === 25 ? ' (mostrando los primeros 25, afina la búsqueda si buscas otro)' : ''}
              </div>
              <div className="buscar-resultados">
                {resultadosBusqueda.map((r) => (
                  <div className="resultado-card" key={r.id}>
                    <div
                      className="resultado-header"
                      onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                    >
                      <div>
                        <div className="resultado-nombre">{r.nombre}</div>
                        <div className="resultado-meta">
                          {r.identificacion && <>ID: <b>{r.identificacion}</b> · </>}
                          Gestor: <b>{r.gestor || 'Sin dato'}</b> · {r.fecha}
                        </div>
                      </div>
                      <div className="resultado-toggle">
                        <i className={`fas fa-chevron-${expandido === r.id ? 'up' : 'down'}`}></i>
                      </div>
                    </div>

                    {expandido === r.id && (
                      <div className="resultado-detalle">
                        {r.detalle.map((campo, idx) => (
                          <div key={idx}>
                            <div className="campo-label">{campo.label}</div>
                            <div className="campo-valor">{campo.valor}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {!buscando && !resultadosBusqueda && (
            <div className="empty-state">Escribe un nombre o número de identificación y presiona Buscar.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBuscar(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {proyecto && (
        <div key={proyecto.key}>
          <h3>
            {proyecto.nombre}
            <button className="proyecto-badge" onClick={() => setShowSelector(true)}>
              <i className="fas fa-repeat"></i> Cambiar proyecto
            </button>
          </h3>
          <p className="subtitle">Descarga y análisis del proyecto seleccionado</p>

          {/* Barra de filtros */}
          <div className="toolbar-card">
            <div>
              <label>Fecha inicio</label>
              <input type="date" className="form-control" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
            </div>
            <div>
              <label>Fecha fin</label>
              <input type="date" className="form-control" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
            </div>
            <Button variant="primary" onClick={handleGenerarDashboard} disabled={cargandoDashboard}>
              <i className="fas fa-chart-column me-1"></i>
              {cargandoDashboard ? 'Generando...' : 'Ver Dashboard'}
            </Button>
            <Button variant="success" onClick={handleDescargar} disabled={descargando}>
              <i className="fas fa-file-excel me-1"></i>
              {descargando ? 'Generando...' : 'Descargar Excel'}
            </Button>
            <Button variant="outline-secondary" onClick={abrirBuscar}>
              <i className="fas fa-magnifying-glass me-1"></i>
              Buscar Persona
            </Button>
          </div>

          {cargandoDashboard && <p className="text-center text-muted">Calculando estadísticas...</p>}

          {stats && (
            <>
              {/* KPIs */}
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-label">Total Registros</div>
                  <div className="kpi-value">{stats.totalRegistros.toLocaleString()}</div>
                </div>
                {stats.kpis.map((kpi) => (
                  <div className="kpi-card" key={kpi.key}>
                    <div className="kpi-label">{kpi.label}</div>
                    <div className="kpi-value">
                      {kpi.esPorcentaje ? `${kpi.valor}%` : kpi.valor.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mapa de calor */}
              <div className="card-block mb-4">
                <div className="card-title">Mapa de calor — Registros por Año y Mes</div>
                {heatmapData ? (
                  <div className="heatmap-scroll">
                    <table className="heatmap-table">
                      <thead>
                        <tr>
                          <th></th>
                          {MESES.map((m) => (
                            <th key={m}>{m}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {heatmapData.aniosOrdenados.map((anio) => (
                          <tr key={anio}>
                            <td className="year-label">{anio}</td>
                            {heatmapData.anios[anio].map((valor, idx) => (
                              <td
                                key={idx}
                                className={`heatmap-cell ${valor === 0 ? 'empty' : ''}`}
                                style={{ background: colorCelda(valor, heatmapData.max) }}
                                title={`${MESES[idx]} ${anio}: ${valor} registros`}
                              >
                                {valor || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">Sin datos para el rango seleccionado</div>
                )}
                <div className="heatmap-legend">
                  Menos
                  <span className="swatch" style={{ background: '#f1f3f5' }}></span>
                  <span className="swatch" style={{ background: '#bfe0f0' }}></span>
                  <span className="swatch" style={{ background: '#6ab0d8' }}></span>
                  <span className="swatch" style={{ background: '#2f7fb3' }}></span>
                  <span className="swatch" style={{ background: '#1e5f8c' }}></span>
                  Más
                </div>
              </div>

              <div className="grid-2">
                {/* Barra */}
                <div className="card-block">
                  <div className="card-title">{stats.graficoBarra?.titulo || 'Distribución'}</div>
                  {barraData.length ? (
                    <ResponsiveContainer width="100%" height={Math.max(220, barraData.length * 46)}>
                      <BarChart data={barraData} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="nombre" width={140} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value.toLocaleString()} />
                        <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={22}>
                          {barraData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="empty-state">Sin datos</div>
                  )}
                </div>

                {/* Donut */}
                <div className="card-block">
                  <div className="card-title">{stats.graficoDonut?.titulo || 'Resumen'}</div>
                  {donutData.length ? (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={donutData} dataKey="valor" nameKey="nombre" innerRadius={55} outerRadius={80} paddingAngle={2}>
                            {donutData.map((entry, idx) => (
                              <Cell key={idx} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => value.toLocaleString()} />
                          <Legend verticalAlign="bottom" height={30} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="donut-center-label">
                        <span className="pct">{donutPctSi}%</span>
                        <small>{stats.graficoDonut?.etiquetaCentral || 'SÍ'}</small>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">Sin datos</div>
                  )}
                </div>
              </div>

              {/* Categorías top */}
              <div className="card-block mb-4">
                <div className="card-title">{stats.graficoCategorias?.titulo || 'Top categorías'}</div>
                {categoriasData.length ? (
                  <ResponsiveContainer width="100%" height={Math.max(240, categoriasData.length * 42)}>
                    <BarChart data={categoriasData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="nombre" width={180} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Bar dataKey="total" fill="#1f3a5f" radius={[0, 6, 6, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="empty-state">Sin datos</div>
                )}
              </div>

              {/* NUEVO: Edad + Tipo de Identificación, lado a lado */}
              {(edadData.length > 0 || categoriasSecundariasData.length > 0) && (
                <div className="grid-2">
                  {edadData.length > 0 && (
                    <div className="card-block">
                      <div className="card-title">{stats.graficoEdad?.titulo || 'Distribución por Edad'}</div>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={edadData} margin={{ top: 10, right: 10, left: -10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="nombre" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={50} />
                          <YAxis allowDecimals={false} />
                          <Tooltip formatter={(value) => value.toLocaleString()} />
                          <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                            {edadData.map((entry, idx) => (
                              <Cell key={idx} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {categoriasSecundariasData.length > 0 && (
                    <div className="card-block">
                      <div className="card-title">{stats.graficoCategoriasSecundario?.titulo || 'Categorías'}</div>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={categoriasSecundariasData} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="nombre" width={110} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(value) => value.toLocaleString()} />
                          <Bar dataKey="total" fill="#7a4fc9" radius={[0, 6, 6, 0]} barSize={18} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}

              {/* NUEVO: Síntomas más frecuentes */}
              {sintomasData.length > 0 && (
                <div className="card-block mb-4">
                  <div className="card-title">{stats.graficoSintomas?.titulo || 'Síntomas más frecuentes'}</div>
                  <ResponsiveContainer width="100%" height={Math.max(240, sintomasData.length * 38)}>
                    <BarChart data={sintomasData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={20}>
                        {sintomasData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* NUEVO: gráficos de categorías adicionales (cualquier cantidad, definidos por config) */}
              {graficosExtra.length > 0 && (
                <div
                  className="mb-4"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '1.5rem',
                  }}
                >
                  {graficosExtra.map((g, gi) => {
                    const datos = (g.datos || []).map((d, idx) => ({ ...d, fill: COLOR_BARRA[idx % COLOR_BARRA.length] }));
                    if (!datos.length) return null;
                    return (
                      <div className="card-block" key={gi}>
                        <div className="card-title">{g.titulo}</div>
                        <ResponsiveContainer width="100%" height={Math.max(200, datos.length * 40)}>
                          <BarChart data={datos} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => value.toLocaleString()} />
                            <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={18}>
                              {datos.map((entry, idx) => (
                                <Cell key={idx} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!stats && !cargandoDashboard && (
            <div className="empty-state">
              Selecciona un rango de fechas y presiona <b>"Ver Dashboard"</b> para ver las estadísticas.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PanelFormatoBAC;